import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { createDM, type OrgMember } from '../../services/chatChannelService';
import { useAuthStore } from '../../store/authStore';
import MemberSearchInput from './MemberSearchInput';
import Spinner from '../ui/Spinner';

interface Props {
  onClose: () => void;
}

export default function CreateDMModal({ onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose]);

  const handleAdd = (member: OrgMember) => {
    setMemberIds((prev) => prev.includes(member.userId) ? prev : [...prev, member.userId]);
  };

  const handleRemove = (userId: string) => {
    setMemberIds((prev) => prev.filter((id) => id !== userId));
  };

  const handleSubmit = async () => {
    if (memberIds.length === 0) { toast.error('대화 상대를 선택하세요.'); return; }
    setLoading(true);
    try {
      const result = await createDM(memberIds);
      queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
      onClose();
      navigate(`/chat/${result.id}`);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'DM 생성에 실패했습니다.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">새 다이렉트 메시지</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="size-5" />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-3">대화 상대 선택 (최대 9명)</p>

        <MemberSearchInput
          selectedIds={memberIds}
          onAdd={handleAdd}
          onRemove={handleRemove}
          excludeIds={currentUser ? [currentUser.id] : []}
          maxCount={9}
        />

        <p className="text-sm text-gray-400 mt-2">{memberIds.length}/9명 선택</p>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
            취소
          </button>
          <button onClick={handleSubmit} disabled={memberIds.length === 0 || loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors">
            {loading && <Spinner className="size-4 text-white" />}
            대화 시작
          </button>
        </div>
      </div>
    </div>
  );
}
